import React, { useState, useEffect, useRef } from 'react'
import { Search, X, Check } from 'react-feather'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'
import {
  useQueryParams,
  NumberParam,
  withDefault,
  JsonParam,
} from 'use-query-params'

import {
  Input,
  useDebounceState,
  usePaginatedProjects,
  useAuthState,
  useOnClickOutside,
  useLocalStorage,
  useGlobalState,
} from '@pepper/ui'
import { fetchList } from '@pepper/utils/src/api/assignments/assignments'

import {
  checkIsValidCompanyId,
  useSelectedCompanyId,
} from 'utils/functions/misc'
import { ReactComponent as ApprovalPendingIcon } from 'assets/images/status_icon.svg'
import { useOrderItems } from 'utils/hooks/useOrderItems'
import { useDocItems } from 'utils/hooks/useDocItems'

import ProjectSearchList from './ProjectSearchList'
import RecentSearchList from './RecentSearchList'
import LoadingSearchList from './LoadingSearchList'
import { OrderSearchList, DocSearchList } from './ItemSearchList'
import s from './universalSearch.module.css'

const PROJECT_PER_PAGE = 15

type UniversalSearchProps = {
  isMobileView: boolean
}

const UniversalSearch: React.FC<UniversalSearchProps> = ({
  isMobileView,
}: UniversalSearchProps) => {
  const [searchText, setSearchText] = useState('')
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [inProject, setInProject] = useState(false)

  const [recentSearches, setRecentSearches] = useLocalStorage<
    Record<string, string[]>
  >('recentSearches', {})
  const squareBoxRef = useRef<HTMLDivElement>(null)
  const clickOutsidehandler = () => {
    setShowDropdownMenu(false)
  }
  useOnClickOutside(squareBoxRef, clickOutsidehandler)
  const history = useHistory()
  const { user } = useAuthState()
  const companyId = useSelectedCompanyId()
  const isValidCompanyId = checkIsValidCompanyId(companyId, user)
  const debouncedSearchValue = useDebounceState<string>(searchText, 800)
  const [assignmentsInProjects, setAssignmentsInProjects] = useState<{
    items: Assignment[]
    total: number
  }>({ items: [], total: 0 })

  const [queryParamFilters] = useQueryParams({
    filters: withDefault(JsonParam, {}),
    sort: withDefault(JsonParam, { sort_order: undefined, sort_by: undefined }),
    currentPage: withDefault(NumberParam, 0),
  })

  const isProjectPage = /\/project\/(?!draft-project)/.test(
    history.location.pathname
  )
  const projectId = isProjectPage && history.location.pathname.split('/')[2]

  const { projects, totalProjects, isProjectsLoading } = usePaginatedProjects(
    {
      current_page: queryParamFilters.currentPage,
      company_id: companyId,
      search_title: debouncedSearchValue,
      page_size: PROJECT_PER_PAGE,
      pagination_enabled: true,
    },
    { enabled: Boolean(companyId) && isValidCompanyId }
  )

  const {
    totalOrders,
    records: assignments,
    refetch: refetchAssignments,
  } = useOrderItems({
    companyId,
    queryParams: {
      search_title: debouncedSearchValue,
    },
    pageSize: PROJECT_PER_PAGE,
    options: {
      isEnabled: debouncedSearchValue.length > 0,
    },
  })

  const { total: totalDocs, records: docItems } = useDocItems({
    companyId,
    queryParams: {
      search_title: debouncedSearchValue,
    },
    pageSize: PROJECT_PER_PAGE,
    options: {
      isEnabled: debouncedSearchValue.length > 0,
    },
  })

  const fetchAssignmentsInProject = async (_projectId: string) => {
    const { status, data } = await fetchList({
      query_params: {
        project_id: _projectId,
        stage: ['all'],
        page_size: 3,
        search_title: debouncedSearchValue,
      },
      isPaginatedList: true,
      isV2: true,
    })

    if (status === 'ok') {
      setAssignmentsInProjects({
        items: data.records,
        total: data.total_records,
      })
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const closeDropdown = () => {
    setSearchText('')
    clickOutsidehandler()
  }

  const pushElementToLocalStorage = (elem: string) => {
    const newArr = recentSearches[companyId] || []
    if (!recentSearches[companyId]) {
      newArr.push(elem)
    } else {
      if (newArr.includes(elem)) newArr.splice(newArr.indexOf(elem), 1)
      if (newArr.length > 4) newArr.splice(-1)
      newArr.unshift(elem)
    }
    setRecentSearches({ ...recentSearches, [companyId]: newArr })
  }

  const { permissions, setAssignmentRefetchFunction, pepperEditorType } =
    useGlobalState()

  useEffect(() => {
    if (projectId && debouncedSearchValue) {
      fetchAssignmentsInProject(projectId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, debouncedSearchValue])

  useEffect(() => {
    if (refetchAssignments) {
      setAssignmentRefetchFunction(refetchAssignments)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchAssignments])

  return (
    <div
      className={clsx(s.searchBar, {
        [s.searchBarDoc]: pepperEditorType,
        [s.isMobile]: isMobileView,
      })}
      ref={squareBoxRef}
    >
      {pepperEditorType && (
        <div
          role="none"
          onClick={() => setShowDropdownMenu(true)}
          className={clsx(s.iconContainer, {
            [s.active]: showDropdownMenu,
          })}
        >
          <Search size={16} onClick={() => setShowDropdownMenu(true)} />
        </div>
      )}
      {!pepperEditorType && (
        <Input
          onChange={handleChange}
          value={searchText}
          className={clsx({
            [s.expanded]: showDropdownMenu,
          })}
          prefix={<Search size={16} />}
          suffix={
            searchText ? (
              <X size={16} onClick={() => setSearchText('')} />
            ) : null
          }
          placeholder="Search for projects or items"
          onFocus={() => setShowDropdownMenu(true)}
          dataId="business-universal-search-search-input"
          autoFocus={isMobileView}
        />
      )}
      {showDropdownMenu && (
        <div
          className={clsx(s.usdropdown, {
            [s.usdropdownDoc]: pepperEditorType,
          })}
        >
          {pepperEditorType && (
            <Input
              onChange={handleChange}
              value={searchText}
              prefix={<Search size={16} />}
              suffix={
                searchText ? (
                  <X size={16} onClick={() => setSearchText('')} />
                ) : null
              }
              placeholder="Search for projects or items"
              onFocus={() => setShowDropdownMenu(true)}
              dataId="business-universal-search-search-input"
              autoFocus={isMobileView}
            />
          )}
          {isProjectPage && (
            <button
              className={clsx(s.searchResultHeader, {
                [s.selectedResultHeader]: inProject,
              })}
              data-id="us_current_project"
              onClick={() => setInProject(!inProject)}
            >
              Search in current project
              {inProject && <Check height={18} />}
            </button>
          )}

          {!isProjectsLoading && !debouncedSearchValue && !isMobileView && (
            <>
              {permissions?.marketplace && (
                <div className={s.quickOptions}>
                  <div className={s.quickOptionsHeader}>
                    Quick search options
                  </div>
                  <div className={s.quickOptionsMenu}>
                    <button
                      className={s.eachQuickOptionsMenu}
                      data-id="business-universal-search-quick-search-option"
                      onClick={() => {
                        closeDropdown()
                        history.push({
                          pathname: '/projects/items',
                          state: { quickFilter: 'approval_pending' },
                        })
                      }}
                    >
                      <ApprovalPendingIcon />
                      <span>Show all items that are pending approval</span>
                    </button>
                  </div>
                </div>
              )}
              <RecentSearchList
                recentSearches={recentSearches[companyId]}
                setSearchText={setSearchText}
              />
            </>
          )}

          {debouncedSearchValue && isProjectsLoading && <LoadingSearchList />}

          {!isProjectsLoading && debouncedSearchValue && (
            <>
              {((totalProjects === 0 && totalOrders === 0 && totalDocs === 0) ||
                (inProject && assignmentsInProjects.total === 0)) && (
                <div className={s.noResult}>No results found.</div>
              )}
              {!inProject && (
                <ProjectSearchList
                  pushElementToLocalStorage={pushElementToLocalStorage}
                  totalRecords={totalProjects}
                  searchText={searchText}
                  records={projects}
                  closeDropdown={closeDropdown}
                />
              )}

              <OrderSearchList
                pushElementToLocalStorage={pushElementToLocalStorage}
                totalResults={
                  inProject ? assignmentsInProjects.total : totalOrders
                }
                searchText={searchText}
                assignments={
                  inProject ? assignmentsInProjects.items : assignments
                }
                closeDropdown={closeDropdown}
              />

              <DocSearchList
                pushElementToLocalStorage={pushElementToLocalStorage}
                totalResults={
                  inProject ? assignmentsInProjects.total : totalDocs
                }
                searchText={searchText}
                assignments={inProject ? assignmentsInProjects.items : docItems}
                companyId={companyId}
                closeDropdown={closeDropdown}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default UniversalSearch
